import React from 'react';
import propTypes from 'prop-types';
import { Card, CardBody, CardImg, CardText, Collapse } from 'reactstrap';
import circleCheck from 'open-iconic/svg/circle-check.svg';

import { noop } from 'lib/noop';
import { IconButton, SectionHeader } from 'ui/Components';
import { expand } from 'ui/Icons';
import { renderArrowStyle } from 'ui/helpers';

import styles from './AMPCard.css';

/**
 * render a card with information about an asset
 * @param {object} props - props passed to this component
 * @returns {React.Component} React Component for AMP Card.
 */
const AMPCard = ({
  ampAssetId,
  assetId,
  assetSrc,
  assetType,
  division,
  fileType,
  height,
  liveDate,
  onExpand,
  onToggleDetails,
  open,
  selected,
  tags,
  width,
  workfrontId
}) => {
  const cardDetails = [
    { label: 'Asset ID', value: assetId },
    { label: 'File Type', value: fileType },
    { label: 'Dimensions', value: `${width} x ${height}` },
    { label: 'Tags', value: tags.join(', ') }
  ];

  if (workfrontId) {
    cardDetails.push({ label: 'Workfront ID', value: workfrontId });
  }
  if (liveDate) {
    cardDetails.push({ label: 'Live Date', value: liveDate });
  }
  if (division) {
    cardDetails.push({ label: 'Division', value: division });
  }
  if (ampAssetId) {
    cardDetails.push({ label: 'AMP Asset ID', value: ampAssetId });
  }
  if (assetType) {
    cardDetails.push({ label: 'Asset Type', value: assetType });
  }

  return (
    <Card className={`border-0 rounded my-2 ${selected ? styles.selectedCard : styles.card}`}>
      <div className={selected ? styles.fullCircle : styles.emptyCircle} />
      {/* TODO replace with svgr */}
      <div
        className={`${styles.checkIcon} ${selected ? styles.selected : styles.notSelected}`}
        dangerouslySetInnerHTML={{ __html: circleCheck }}
      />
      <IconButton
        className={styles.expandIcon}
        data-asset-src={assetSrc}
        data-test-id="amp-card-expand-icon"
        icon={expand}
        noMargin
        onClick={onExpand}
        rem={1.5}
      />
      <div className="m-5">
        <CardImg alt={assetId} src={assetSrc} top />
      </div>
      <SectionHeader
        data-asset-id={assetId}
        data-test-id="amp-card-collapse-header"
        onClick={onToggleDetails}
      >
        Details
        {renderArrowStyle(open)}
      </SectionHeader>
      <Collapse isOpen={open}>
        <CardBody>
          {cardDetails.map(({ label, value }) => (
            <CardText key={label}>
              <strong>{`${label}: `}</strong>
              {value}
            </CardText>
          ))}
        </CardBody>
      </Collapse>
    </Card>
  );
};

AMPCard.propTypes = {
  ampAssetId: propTypes.string,
  assetId: propTypes.string,
  assetSrc: propTypes.string,
  assetType: propTypes.string,
  division: propTypes.string,
  fileType: propTypes.string,
  height: propTypes.number,
  liveDate: propTypes.string,
  onExpand: propTypes.func,
  onToggleDetails: propTypes.func,
  open: propTypes.bool,
  selected: propTypes.bool,
  tags: propTypes.arrayOf(propTypes.string),
  width: propTypes.number,
  workfrontId: propTypes.number
};

AMPCard.defaultProps = {
  ampAssetId: '',
  assetId: '',
  assetSrc: '',
  assetType: '',
  division: '',
  fileType: '',
  height: 0,
  liveDate: '',
  onExpand: noop,
  onToggleDetails: noop,
  open: true,
  selected: false,
  tags: [],
  width: 0,
  workfrontId: 0
};

export default AMPCard;
